import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { Shield, ToggleLeft, ToggleRight, Loader2, Sparkles, Building, Mail, Award, CheckCircle, Search, X, Pencil, Trash2, Plus } from 'lucide-react';

export default function SuperAdmin() {
  const { user } = useAuth();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  // New Company form state
  const [newCompName, setNewCompName] = useState('');
  const [newLeadName, setNewLeadName] = useState('');
  const [newLeadEmail, setNewLeadEmail] = useState('');
  const [newLeadPassword, setNewLeadPassword] = useState('');

  // Edit Company form state
  const [editCompName, setEditCompName] = useState('');
  const [editCompWebsite, setEditCompWebsite] = useState('');
  const [editCompPhone, setEditCompPhone] = useState('');
  const [editCompTimezone, setEditCompTimezone] = useState('');
  const [editCompAddress, setEditCompAddress] = useState('');
  const [editCompLogoUrl, setEditCompLogoUrl] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/superadmin/companies');
      setCompanies(data.companies || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load organization data.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleToggleActive = async (userId, currentActive) => {
    try {
      setError('');
      setSuccess('');
      await api.post(`/superadmin/users/${userId}/toggle-active`, { is_active: !currentActive });
      setSuccess('User active status updated successfully!');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to update user status.');
    }
  };

  const handleRoleChange = async (userId, role) => {
    try {
      setError('');
      setSuccess('');
      await api.post(`/superadmin/users/${userId}/role`, { role });
      setSuccess('User role reassigned successfully!');
      load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to assign user role.');
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      await api.post('/superadmin/companies', {
        name: newCompName,
        leader_name: newLeadName,
        leader_email: newLeadEmail,
        leader_password: newLeadPassword,
      });
      setSuccess(`Company "${newCompName}" created with bootstrap leader account successfully!`);
      setShowCreateModal(false);
      setNewCompName('');
      setNewLeadName('');
      setNewLeadEmail('');
      setNewLeadPassword('');
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create company.');
    }
  };

  const handleEditCompany = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');
      await api.patch(`/superadmin/companies/${selectedCompany.id}`, {
        name: editCompName,
        website: editCompWebsite,
        phone: editCompPhone,
        timezone: editCompTimezone,
        address: editCompAddress,
        logo_url: editCompLogoUrl,
      });
      setSuccess(`Company "${editCompName}" updated successfully!`);
      setShowEditModal(false);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update company.');
    }
  };

  const handleDeleteCompany = async (companyId, companyName) => {
    if (companyId === user.company_id) {
      setError('Cannot delete the company you are currently logged into.');
      return;
    }
    if (!window.confirm(`Are you absolutely sure you want to delete the company "${companyName}"? This will permanently delete all associated users, projects, and data!`)) {
      return;
    }
    try {
      setError('');
      setSuccess('');
      await api.delete(`/superadmin/companies/${companyId}`);
      setSuccess(`Company "${companyName}" deleted successfully!`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete company.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="animate-spin text-blue-500" size={36} />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="superadmin-dashboard">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight flex items-center gap-2" style={{ margin: 0 }}>
            <Shield className="text-indigo-400" size={28} style={{ display: 'inline', verticalAlign: -4 }} /> Super Admin Dashboard
          </h1>
          <p className="text-slate-300 text-sm mt-1.5" style={{ margin: 0, opacity: 0.85 }}>
            Global Tenant Console · Manage all workspaces, assign system roles, and control leader access.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowCreateModal(true)}
            className="tm-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 10 }}
            data-testid="create-company-btn"
          >
            <Plus size={16} /> Create Company
          </button>
          <div className="px-4 py-2 bg-slate-800/40 border border-slate-700/60 rounded-xl flex items-center gap-2">
            <Sparkles className="text-yellow-400" size={16} />
            <span className="text-xs text-yellow-100 font-semibold">Super User: {user.full_name}</span>
          </div>
        </div>
      </div>

      {/* Search Input Bar */}
      <div style={{
        marginBottom: 24,
        background: 'rgba(255, 255, 255, 0.08)',
        borderRadius: 12,
        padding: '12px 18px',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        border: '1px solid rgba(255, 255, 255, 0.12)'
      }}>
        <Search size={18} style={{ color: '#A0AEC0' }} />
        <input
          type="text"
          placeholder="Search team members by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{
            flex: 1,
            background: 'transparent',
            border: 'none',
            outline: 'none',
            color: '#fff',
            fontSize: 14
          }}
          data-testid="superadmin-search-input"
        />
        {searchQuery && (
          <button
            onClick={() => setSearchQuery('')}
            style={{ background: 'none', border: 'none', color: '#A0AEC0', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}
          >
            <X size={18} />
          </button>
        )}
      </div>

      {error && (
        <div className="p-4 mb-6 bg-red-950/40 border border-red-500/30 text-red-300 rounded-xl text-sm flex items-center gap-2 animate-pulse">
          ❌ {error}
        </div>
      )}

      {success && (
        <div className="p-4 mb-6 bg-emerald-950/40 border border-emerald-500/30 text-emerald-300 rounded-xl text-sm flex items-center gap-2">
          <CheckCircle size={16} className="text-emerald-400" /> {success}
        </div>
      )}

      <div className="space-y-8" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {companies.map((company) => (
          <div key={company.id} className="shadow-xl rounded-2xl" style={{ background: 'var(--trello-card-bg)', border: '1px solid var(--trello-border)', borderLeft: '4px solid var(--trello-blue)', padding: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, borderBottom: '1px solid var(--trello-border)', paddingBottom: 16 }}>
              <div className="flex items-center gap-3" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <div className="p-2.5 bg-blue-600/10 border border-blue-500/20 rounded-xl text-blue-400" style={{ padding: 10, background: 'rgba(0,121,191,0.1)' }}>
                  <Building size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-bold tracking-tight" style={{ margin: 0, fontSize: 18, color: 'var(--trello-text)' }}>{company.name}</h3>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--trello-text-muted)' }}>{company.users.length} members registered</p>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <span style={{ background: 'rgba(120,120,120,0.1)', color: 'var(--trello-text-muted)', border: '1px solid var(--trello-border)', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Tenant ID: {company.id.slice(0, 8)}...
                </span>
                <button
                  onClick={() => {
                    setSelectedCompany(company);
                    setEditCompName(company.name || '');
                    setEditCompWebsite(company.website || '');
                    setEditCompPhone(company.phone || '');
                    setEditCompTimezone(company.timezone || 'UTC');
                    setEditCompAddress(company.address || '');
                    setEditCompLogoUrl(company.logo_url || '');
                    setShowEditModal(true);
                  }}
                  className="p-1.5 hover:bg-slate-700/20 rounded text-blue-500 hover:text-blue-600 transition"
                  title="Edit Company Details"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: 'var(--trello-blue)' }}
                  data-testid={`edit-company-${company.id}`}
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={() => handleDeleteCompany(company.id, company.name)}
                  className="p-1.5 hover:bg-red-500/20 rounded text-red-500 hover:text-red-600 transition"
                  title="Delete Company"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', color: '#DE350B' }}
                  data-testid={`delete-company-${company.id}`}
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>

            {(() => {
              const filteredUsers = company.users.filter((member) =>
                member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                member.email?.toLowerCase().includes(searchQuery.toLowerCase())
              );

              if (company.users.length === 0) {
                return <p className="text-slate-500 text-sm py-4 text-center" style={{ margin: 0 }}>No registered users in this company.</p>;
              }
              if (filteredUsers.length === 0) {
                return <p className="text-slate-500 text-sm py-4 text-center" style={{ margin: 0 }}>No matching members found.</p>;
              }

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {filteredUsers.map((member, memberIdx) => (
                    <div key={member.id} className="flex items-center justify-between" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderTop: memberIdx > 0 ? '1px solid var(--trello-border)' : 'none', paddingTop: memberIdx > 0 ? 12 : 8 }} data-testid={`superadmin-member-row-${member.id}`}>
                      <div className="flex items-center gap-3" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                        <div className="tm-avatar" style={{ background: member.avatar_color || 'var(--trello-blue)' }}>
                          {(member.full_name || member.email).slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-semibold flex items-center gap-2" style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--trello-text)' }}>
                            {member.full_name || member.email.split('@')[0]}
                            {member.role === 'leader' && (
                              <span className="px-2 py-0.5 bg-red-600/10 border border-red-500/20 text-red-400 text-[10px] font-extrabold uppercase rounded" style={{ background: 'rgba(222,53,11,0.15)', border: '1px solid rgba(222,53,11,0.3)', color: '#FF8F73', padding: '1px 6px', borderRadius: 3 }}>
                                Leader
                              </span>
                            )}
                          </div>
                          <div style={{ color: 'var(--trello-text-muted)', fontSize: 12, display: 'flex', alignItems: 'center', marginTop: 4 }}>
                            <Mail size={12} style={{ display: 'inline', verticalAlign: -2, marginRight: 4 }} /> {member.email}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-6" style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
                        {/* Role selection dropdown */}
                        <div className="flex items-center gap-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ color: 'var(--trello-text-muted)', fontSize: 12 }}>Role:</span>
                          <select
                            className="tm-input rounded-lg text-xs"
                            style={{ width: 140, padding: '4px 8px', background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                            value={member.role}
                            onChange={(e) => handleRoleChange(member.id, e.target.value)}
                          >
                            <option value="leader">Leader</option>
                            <option value="manager">Manager</option>
                            <option value="team">Team Member</option>
                          </select>
                        </div>

                        {/* Active toggle button */}
                        <div className="flex items-center gap-2" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ color: 'var(--trello-text-muted)', fontSize: 12 }}>Status:</span>
                          <button
                            onClick={() => handleToggleActive(member.id, member.is_active)}
                            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
                            title={member.is_active ? 'Deactivate User' : 'Activate User'}
                          >
                            {member.is_active ? (
                              <ToggleRight className="text-emerald-400 hover:text-emerald-300" size={32} style={{ color: '#48BB78' }} />
                            ) : (
                              <ToggleLeft className="text-slate-500 hover:text-slate-400" size={32} style={{ color: '#A0AEC0' }} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>
        ))}
      </div>

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="tm-modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="tm-modal" style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)', padding: 24, borderRadius: 12, width: '100%', maxWidth: 500 }} data-testid="create-company-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Create New Tenant Company</h3>
              <button onClick={() => setShowCreateModal(false)} style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleCreateCompany} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Company Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Acme Corp"
                  className="tm-input"
                  style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                  value={newCompName}
                  onChange={(e) => setNewCompName(e.target.value)}
                  data-testid="create-company-name-input"
                />
              </div>

              <div style={{ borderTop: '1px solid var(--trello-border)', paddingTop: 16 }}>
                <h4 style={{ margin: '0 0 12px 0', fontSize: 13, fontWeight: 700, color: 'var(--trello-text-muted)' }}>Bootstrap Leader Account</h4>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Leader Name</label>
                    <input
                      type="text"
                      required
                      placeholder="e.g. John Doe"
                      className="tm-input"
                      style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                      value={newLeadName}
                      onChange={(e) => setNewLeadName(e.target.value)}
                      data-testid="create-leader-name-input"
                    />
                  </div>
                  <div>
                    <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Leader Email</label>
                    <input
                      type="email"
                      required
                      placeholder="leader@acme.com"
                      className="tm-input"
                      style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                      value={newLeadEmail}
                      onChange={(e) => setNewLeadEmail(e.target.value)}
                      data-testid="create-leader-email-input"
                    />
                  </div>
                  <div>
                    <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Password</label>
                    <input
                      type="password"
                      required
                      placeholder="••••••••"
                      className="tm-input"
                      style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                      value={newLeadPassword}
                      onChange={(e) => setNewLeadPassword(e.target.value)}
                      data-testid="create-leader-password-input"
                    />
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                <button type="button" className="tm-btn-ghost" onClick={() => setShowCreateModal(false)} style={{ color: 'var(--trello-text)' }}>Cancel</button>
                <button type="submit" className="tm-btn" data-testid="create-company-submit-btn">Create Company</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal */}
      {showEditModal && selectedCompany && (
        <div className="tm-modal-overlay" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="tm-modal" style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)', padding: 24, borderRadius: 12, width: '100%', maxWidth: 500 }} data-testid="edit-company-modal">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Edit Company Profile</h3>
              <button onClick={() => setShowEditModal(false)} style={{ background: 'none', border: 'none', color: 'var(--trello-text-muted)', cursor: 'pointer' }}><X size={20} /></button>
            </div>
            <form onSubmit={handleEditCompany} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Company Name</label>
                <input
                  type="text"
                  required
                  className="tm-input"
                  style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                  value={editCompName}
                  onChange={(e) => setEditCompName(e.target.value)}
                  data-testid="edit-company-name-input"
                />
              </div>

              <div>
                <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Logo URL</label>
                <input
                  type="text"
                  placeholder="https://example.com/logo.png"
                  className="tm-input"
                  style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                  value={editCompLogoUrl}
                  onChange={(e) => setEditCompLogoUrl(e.target.value)}
                  data-testid="edit-company-logo-input"
                />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Website</label>
                  <input
                    type="text"
                    placeholder="https://example.com"
                    className="tm-input"
                    style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                    value={editCompWebsite}
                    onChange={(e) => setEditCompWebsite(e.target.value)}
                    data-testid="edit-company-website-input"
                  />
                </div>
                <div>
                  <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Phone</label>
                  <input
                    type="text"
                    placeholder="+1 (555) 0199"
                    className="tm-input"
                    style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                    value={editCompPhone}
                    onChange={(e) => setEditCompPhone(e.target.value)}
                    data-testid="edit-company-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Timezone</label>
                <input
                  type="text"
                  placeholder="UTC"
                  className="tm-input"
                  style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)' }}
                  value={editCompTimezone}
                  onChange={(e) => setEditCompTimezone(e.target.value)}
                  data-testid="edit-company-timezone-input"
                />
              </div>

              <div>
                <label className="tm-label" style={{ color: 'var(--trello-text-muted)', fontSize: 11 }}>Address</label>
                <textarea
                  rows={3}
                  placeholder="123 Enterprise Way, City, Country"
                  className="tm-input"
                  style={{ background: 'var(--trello-card-bg)', color: 'var(--trello-text)', border: '1px solid var(--trello-border)', fontFamily: 'inherit' }}
                  value={editCompAddress}
                  onChange={(e) => setEditCompAddress(e.target.value)}
                  data-testid="edit-company-address-input"
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 12 }}>
                <button type="button" className="tm-btn-ghost" onClick={() => setShowEditModal(false)} style={{ color: 'var(--trello-text)' }}>Cancel</button>
                <button type="submit" className="tm-btn" data-testid="edit-company-submit-btn">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
